
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import ContractProductsFiltersMixins from "@/mixins/provisioning/contractProductsFiltersMixins";
import Context from "@/mixins/context";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { QUERY_PARAMS } from "@/data/constants";
import { ProvisionRequestStatusCodes as PRSC } from "@/data/enums/contractProducts";
import clientFilters from "@/data/filters/client";
import contractProductRequestsFilters from "@/data/filters/contractProductRequests";
import contractProductRequestsSorters from "@/data/sorters/contractProductRequests";
import { serialiseConfig } from "@/helpers/table";
import { FilterOperators, Sorters } from "@/data/table";

// Types
import {
  IProvisionRequestsProvider,
  IProvisionRequestsProviderMethods
} from "@/models/providers/provisionRequests";
import { ISorter, IFilter } from "@/models/tables";
import { IContract, IContractProduct } from "@/models/contracts";
import { IClient } from "@/models/clients";
import { IProvisionRequest } from "@/models/provisioning";
import { IProductCategory } from "@/models/products";

@Component
export default class ProvisionRequestsProvider extends Mixins(
  FilteringSortingTableStore,
  ContractProductsFiltersMixins,
  Context
) {
  // Props

  @Prop({ default: "PRLP1" /** [P]rovision [R]equests [L]ist [P]rovider */ })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  @Prop({ default: undefined })
  public contractId!: IContract["id"];

  @Prop({ default: undefined })
  public productId!: IContractProduct["id"];

  @Prop({ default: "" })
  public clientId!: IClient["id"];

  @Prop({ default: false })
  public withClientFilters!: number;

  @Prop({ default: false })
  public isStaged!: boolean;

  @Prop({ default: true })
  public routeOpening!: boolean;

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  public isRetrying = {};

  public dataModule: string = DataModules.PROVISIONING_CONTRACT_PRODUCTS;
  public filters: IFilter[] = [];
  public sorters: ISorter[] = [];
  public defaults = { sort: contractProductRequestsSorters[0] };

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.checkRequestModalOpen();
    await this.loadData();

    this.$bus.$on("refreshRequestTable", this.refreshRequestsTable);
  }

  public beforeDestroy() {
    this.$bus.$off("refreshRequestTable");
  }

  // Methods

  public async loadData() {
    try {
      await this.getFiltersData(this.isReloading);

      this.setFiltersAndSorters();

      if (this.contractId && this.productId) {
        this.dataPayloadSettings = {
          contractId: this.contractId,
          productId: this.productId
          // ignoreStored: true // check is needed
        };
      }

      this.apiProps = {
        with: [
          "actor",
          "blueprint_function",
          "configuration",
          "order_product",
          "order_product.product.image",
          "provider.logos",
          "status"
        ].join(","),
        /** Filter out draft requests */
        "filter[status.code|neq]": PRSC.PROVISION_REQUEST_DRAFT,
        with_staged_imports: this.isStaged ? 1 : 0
      };

      await this.getData({ ignoreStored: this.isReloading });

      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.cancelRequests();
      return this.getData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  public setFiltersAndSorters() {
    this.filters = _.concat(
      contractProductRequestsFilters,
      !this.withClientFilters
        ? []
        : clientFilters().map(i => ({
            ...i,
            label: `${this.$t("_.client")}: ${i.label}`,
            key: `client.${i.key}`
          })),
      _.map(this.createProvisionFilters(false), filter => {
        filter.key = filter.key
          .replace(
            "product.provision_blueprint_id",
            "blueprint_function.blueprint_id"
          )
          .replace("provision_provider_id", "provider_id")
          .replace("provision_configuration_id", "configuration_id"); // Update keys, because of different data structure than contract product reused code
        return filter;
      })
    );

    this.sorters = contractProductRequestsSorters;
  }

  public refreshRequestsTable() {
    return this.getData({ ignoreStored: true });
  }

  public getStoreParams(params: any): Promise<any> {
    return _.merge(params, {
      params: {
        ...this.getFilterForClient()
      }
    });
  }

  public getFilterForClient() {
    return this.clientId ? { "filter[client_id]": this.clientId } : {};
  }

  public checkRequestModalOpen() {
    if (!this.routeOpening || !this.routeModalRequestId) return;
    this.viewRequest(this.routeModalRequestId);
  }

  public viewRequest(requestId: IProvisionRequest["id"]) {
    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_REQUESTS}/openRequestModal`,
      {
        props: {
          requestId,
          routeOpening: this.routeOpening
        },
        events: { success: this.refreshRequestsTable }
      }
    );
  }

  public getCategoryName(categoryId: IProductCategory["id"]) {
    return _.find(this.categories, ["id", categoryId])?.name;
  }

  public getCategoryRoute(categoryId: IProductCategory["id"]) {
    return {
      name: "adminCatalogueProvisioning",
      query: {
        PCLP1: serialiseConfig({
          page: 1,
          limit: 20,
          sort: {
            field: "name",
            direction: Sorters.ASCENDING
          },
          filters: [
            {
              key: "category.code",
              operator: FilterOperators.EQUAL,
              value: _.find(this.categories, ["id", categoryId])?.code
            }
          ]
        })
      }
    };
  }

  // Computed

  public get routeModalRequestId() {
    return _.get(this.$route, `query.${QUERY_PARAMS.MODAL_REQUEST_ID}`, null);
  }

  // Guarding

  public guardFunctionalities = [
    "list_contract_product_provision_requests",
    "list_provision_blueprints",
    "list_provision_categories",
    "list_provision_provider_configurations"
  ];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("provisionRequestsProvider")
  public providerData(): IProvisionRequestsProvider {
    return {
      availableFilters: this.filters,
      availableSorters: this.sorters,
      activeFilters: this.activeFilters,
      constQueryFilters: this.constQueryFilters,
      activeSort: this.table.sort,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      isLoading: this.table.loading || this.isLoading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      productId: this.productId,
      provisionRequests: this.table.data as IProvisionRequest[],
      table: this.table,
      total: this.table.total,
      guardFunctionalities: this.guardFunctionalities
    };
  }

  @Provide("provisionRequestsProviderMethods")
  public get providerMethods(): IProvisionRequestsProviderMethods {
    return {
      getCategoryName: this.getCategoryName,
      getCategoryRoute: this.getCategoryRoute,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData,
      viewRequest: this.viewRequest
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.loadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $requestsData: this.providerData(),
        $requestsMethods: this.providerMethods
      })
    );
  }
}
